import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle, LogOut } from 'lucide-react';
import { SidebarNavItem } from './SidebarNavItem';
import { getParentId, isSubMenuItem, type NavItem } from '../src/utils/navigationConfig';
import { getSupabase } from '@/lib/supabase';
import { clearPendingInvite } from '@/utils/pendingInvite';
import { ThemeToggle, ThemeToggleCompact } from '../src/components/ThemeToggle';
import { signOutOnServer } from '@/data/authApi';

interface SidebarProps {
  items: NavItem[];
  activeTab: string;
  onNavigate: (id: string) => void;
  onVisualStateChange?: (isExpanded: boolean) => void;
  /** Where to send the user after sign-out (desktop sidebar). */
  signOutRedirectTo?: string;
}

export function Sidebar({ items, activeTab, onNavigate, onVisualStateChange, signOutRedirectTo }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Determine if the active tab is a submenu item and what its parent is
  const isSubItem = isSubMenuItem(activeTab, items);
  const parentId = isSubItem ? getParentId(activeTab) : activeTab;

  // Visual state: expand if either permanently expanded OR (collapsed but hovered)
  const isVisuallyExpanded = !isCollapsed || (isCollapsed && isHovered);

  // Notify parent component when visual state changes
  React.useEffect(() => {
    onVisualStateChange?.(isVisuallyExpanded);
  }, [isVisuallyExpanded, onVisualStateChange]);

  const handleSignOut = async () => {
    // 1. Clear httpOnly cookies via Worker (best effort; never throws)
    await signOutOnServer();
    // 2. Clear Supabase client-side session
    try {
      const supabase = getSupabase();
      if (supabase) await supabase.auth.signOut();
    } catch {
      /* best effort */
    }
    // 3. Redirect target (read before clearing stored role)
    let dest = signOutRedirectTo;
    if (!dest) {
      try {
        dest = localStorage.getItem('coachd:userType') === 'client' ? '/client/auth' : '/coach/sign-in';
      } catch {
        dest = '/coach/sign-in';
      }
    }
    // 4. Clear persisted role + any stale pending invite keys
    try {
      localStorage.removeItem('coachd:userType');
    } catch {
      /* ignored */
    }
    clearPendingInvite();
    window.location.replace(dest);
  };

  return (
    <aside
      className={`
        fixed top-0 left-0 h-screen bg-sidebar text-sidebar-foreground border-r border-sidebar-border shadow-[2px_0_16px_rgba(15,23,42,0.06)] dark:shadow-none flex flex-col overflow-y-auto
        transition-all duration-300 ease-in-out z-40
        ${isVisuallyExpanded ? 'w-60' : 'w-16'}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo/Brand — app mark (rounded icon); word uses theme text; icon keeps color in both themes */}
      <div className={`p-4 border-b border-sidebar-border ${!isVisuallyExpanded ? 'flex justify-center' : ''}`}>
        <div className={`flex items-center gap-2.5 ${isVisuallyExpanded ? 'justify-start' : 'justify-center'}`}>
          <img
            src="/branding/coachos-icon.png"
            alt="CoachOS"
            width={36}
            height={36}
            className="h-9 w-9 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-black/5 dark:ring-white/10"
            decoding="async"
          />
          {isVisuallyExpanded && (
            <span className="text-lg font-semibold tracking-tight text-foreground whitespace-nowrap">CoachOS</span>
          )}
        </div>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 py-2">
        {items.map((item) => {
          const isActive = item.id === parentId && !isSubItem;
          const isSubItemActive = item.id === parentId && isSubItem;
          const activeSubItemId = isSubItemActive ? activeTab : undefined;

          return (
            <SidebarNavItem
              key={item.id}
              item={item}
              isActive={isActive}
              isSubItemActive={isSubItemActive}
              activeSubItemId={activeSubItemId}
              onNavigate={onNavigate}
              isCollapsed={!isVisuallyExpanded}
            />
          );
        })}
      </nav>

      {/* Theme toggle */}
      <div className="border-t border-sidebar-border px-3 py-3">
        {isVisuallyExpanded ? (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Appearance</p>
            <ThemeToggle />
          </div>
        ) : (
          <div className="flex justify-center">
            <ThemeToggleCompact />
          </div>
        )}
      </div>

      {/* Help link (help center on the marketing site) */}
      <div className="border-t border-sidebar-border">
        <a
          href="https://os-coach.com/help"
          target="_blank"
          rel="noreferrer"
          className={`
            w-full flex items-center py-3 px-4
            hover:bg-secondary transition-colors duration-200
            text-muted-foreground hover:text-foreground
            ${!isVisuallyExpanded ? 'justify-center' : 'justify-start'}
          `}
          title="Help"
        >
          <HelpCircle className={`w-5 h-5 ${isVisuallyExpanded ? 'mr-2' : ''}`} />
          {isVisuallyExpanded && <span className="text-sm">Help</span>}
        </a>
      </div>

      {/* Sign Out button */}
      <div className="border-t border-sidebar-border">
        <button
          onClick={handleSignOut}
          className={`
            w-full flex items-center py-3 px-4
            hover:bg-destructive/10 transition-colors duration-200
            text-destructive hover:text-destructive
            ${!isVisuallyExpanded ? 'justify-center' : 'justify-start'}
          `}
          title="Sign Out"
        >
          <LogOut className={`w-5 h-5 ${isVisuallyExpanded ? 'mr-2' : ''}`} />
          {isVisuallyExpanded && <span className="text-sm">Sign Out</span>}
        </button>
      </div>

      {/* Toggle button */}
      <div className="border-t border-sidebar-border">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`
            w-full flex items-center justify-center py-3 px-4
            hover:bg-secondary transition-colors duration-200
            text-muted-foreground hover:text-foreground
          `}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5 mr-2" />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
